/**
 * Slice LV-02 · leviSpeech — thin wrapper around window.speechSynthesis
 * with default voice selection, TTS-priming for iOS user-gesture
 * requirement, stop-speaking, ducking, and a persisted voice-enabled
 * preference.
 *
 * Live-runtime failure investigation (PO QA fix):
 * The most common cause of "text arrives, audio doesn't" on Levi is
 * iOS Safari's user-gesture policy: the FIRST speechSynthesis.speak()
 * must fire inside a synchronous user gesture (touchend/click). Our
 * flow instead calls speak() AFTER `await api.post(...)`, which is
 * outside the gesture stack and iOS Safari silently drops the
 * utterance. We fix this with a silent-primer utterance triggered
 * INSIDE the user gesture (mic tap, test button, typed submit,
 * confirmation button) — it unlocks the audio session for the tab so
 * subsequent async speaks are honored.
 *
 * Diagnostics: every material event is pushed to a bounded ring
 * buffer accessible via getDiag()/window.__leviDiag. The buffer is
 * always kept in memory but is only surfaced in production copy when
 * the URL contains ?leviDiag=1 (or NODE_ENV !== 'production').
 */
const PREF_KEY = "jc:levi:tts-enabled";

// ---------- Diagnostic ring buffer ----------
const DIAG_LIMIT = 200;
const DIAG_WINDOW_MS = 10 * 60 * 1000;
const _diagBuf = [];
const TURN_DIAG_BUCKET_LIMIT = 2;
const _turnDiagBuckets = new Map();
let _reportableTurnId = null;

const _pruneTurnDiagBuckets = (now = Date.now()) => {
  const cutoff = now - DIAG_WINDOW_MS;
  for (const [turnId, bucket] of _turnDiagBuckets.entries()) {
    if (!bucket || bucket.last_ts < cutoff) {
      _turnDiagBuckets.delete(turnId);
      if (_reportableTurnId === turnId) _reportableTurnId = null;
    }
  }
  if (_turnDiagBuckets.size <= TURN_DIAG_BUCKET_LIMIT) return;
  const activeId = (() => {
    try { return window.__leviActiveSpeechTurn?.id || null; }
    catch { return null; }
  })();
  const removable = [..._turnDiagBuckets.entries()]
    .filter(([turnId]) => turnId !== activeId && turnId !== _reportableTurnId)
    .sort((a, b) => a[1].last_ts - b[1].last_ts);
  while (_turnDiagBuckets.size > TURN_DIAG_BUCKET_LIMIT && removable.length) {
    _turnDiagBuckets.delete(removable.shift()[0]);
  }
};

const _retainTurnEntry = (entry) => {
  const turnId = entry?.client_speech_turn_id;
  if (!turnId) return;
  const bucket = _turnDiagBuckets.get(turnId) || {
    turn_id: turnId,
    listening_epoch: entry.listening_epoch || null,
    first_ts: entry.ts,
    last_ts: entry.ts,
    events: [],
  };
  if (entry.listening_epoch != null && (bucket.listening_epoch == null
      || entry.evt === "listening:epoch_start" || entry.evt === "vad:endpoint")) {
    bucket.listening_epoch = entry.listening_epoch;
  }
  bucket.last_ts = entry.ts;
  bucket.events.push(entry);
  _turnDiagBuckets.set(turnId, bucket);
  if ((entry.evt === "vad:endpoint" && entry.voice_ever === true)
      || entry.evt === "voice_submit:requested"
      || (entry.evt === "command_submission:started" && entry.has_audio_file === true)) {
    _reportableTurnId = turnId;
  }
  _pruneTurnDiagBuckets(entry.ts);
};
const _diagEnabled = () => {
  try {
    // PO 2026-02 · Bounded Preview-only QA instrumentation flag.
    // Production `.env` never sets this; Preview `.env` opts in so
    // manual QA (Gabe's Roofing acceptance) sees the voice diag panel
    // without ?leviDiag=1 / localStorage / DevTools.
    if (String(process.env.REACT_APP_LEVI_VOICE_DIAG_ENABLED || "")
          .toLowerCase() === "true") {
      return true;
    }
    if (typeof window !== "undefined") {
      if (window.location?.search?.includes("leviDiag=1")) return true;
      try { if (localStorage.getItem("jc:levi:diag") === "1") return true; } catch { /* noop */ }
    }
  } catch { /* noop */ }
  return false;
};
// Public gate for contractor-facing UI. LV-02 lock scope hides the
// diagnostic panel from the production experience; it stays reachable
// behind the internal developer flag (?leviDiag=1 or the sticky
// localStorage key jc:levi:diag=1). DEV/preview builds also hide the
// panel unless one of those explicit developer flags is set.
export const isDiagEnabled = _diagEnabled;
// If the URL carries ?leviDiag=1 at first module load, persist it so
// SPA navigation to routes without the query still surfaces the panel
// and console logs. ?leviDiag=0 clears it.
try {
  if (typeof window !== "undefined" && window.location?.search) {
    if (window.location.search.includes("leviDiag=1")) {
      try { localStorage.setItem("jc:levi:diag", "1"); } catch { /* noop */ }
    } else if (window.location.search.includes("leviDiag=0")) {
      try { localStorage.removeItem("jc:levi:diag"); } catch { /* noop */ }
    }
  }
} catch { /* noop */ }
const _pushDiag = (evt, data) => {
  const ts = Date.now();
  let monoMs = null;
  try { monoMs = performance.now(); } catch { /* noop */ }
  let context = {};
  try {
    const qa = (typeof window !== "undefined" && window.__leviQAState) || {};
    const activeTurn = (typeof window !== "undefined"
      && (window.__leviActiveSpeechTurn || window.__leviLastSpeechTurn)) || {};
    context = {
      session_id: qa.levi_session_id || qa.session_id || null,
      client_speech_turn_id: activeTurn.id || null,
      listening_epoch: activeTurn.listening_epoch || qa.listening?.epoch || null,
    };
  } catch { /* noop */ }
  const entry = {
    ts,
    wall_clock: new Date(ts).toISOString(),
    monotonic_ms: monoMs,
    evt,
    ...context,
    ...(data || {}),
  };
  _diagBuf.push(entry);
  _retainTurnEntry(entry);
  while (_diagBuf.length > DIAG_LIMIT
      || (_diagBuf.length && ts - _diagBuf[0].ts > DIAG_WINDOW_MS)) {
    _diagBuf.shift();
  }
  if (_diagEnabled()) {
    try { console.debug("[leviSpeech]", evt, data || ""); } catch { /* noop */ }
  }
  try {
    if (typeof window !== "undefined") window.__leviDiag = _diagBuf;
  } catch { /* noop */ }
};
export const getDiag = (n = 30, maxAgeMs = DIAG_WINDOW_MS) => {
  const cutoff = Date.now() - Math.max(0, Number(maxAgeMs) || 0);
  return _diagBuf.filter((entry) => entry.ts >= cutoff).slice(-n);
};
export const getReportableTurnDiag = (maxAgeMs = DIAG_WINDOW_MS) => {
  _pruneTurnDiagBuckets();
  let activeTurnId = null;
  try { activeTurnId = window.__leviActiveSpeechTurn?.id || null; }
  catch { activeTurnId = null; }
  // PO 2026-09-24 · SDR-8F8B diagnostic-capture fix (customer-authorized,
  // Preview-only, capture/report plumbing only — no VAD behavior change).
  // An in-progress/stuck turn distinct from the last-submitted one must
  // win selection: otherwise "Report a Problem" clicked while a turn is
  // still stuck captures the prior, already-fine turn instead of the one
  // the user is actually complaining about. Falls back to the
  // last-submitted turn (then to a bare active id) exactly as before
  // when no distinct, retained active turn exists.
  const activeBucket = activeTurnId ? _turnDiagBuckets.get(activeTurnId) : null;
  // Require genuine turn-start lifecycle evidence, not just any event
  // incidentally tagged with the current active id — pushDiag() default-
  // tags every event (including generic/global ones) with whatever turn is
  // currently active, so an auto-rearmed turn with no real capture activity
  // of its own must not outrank an already-submitted turn.
  const activeHasTurnLifecycle = !!(activeBucket && activeBucket.events.some(
    (e) => e.evt === "mic:acquire_requested"
        || e.evt === "listening:epoch_start"
        || e.evt === "vad:armed"));
  const preferActive = activeHasTurnLifecycle && activeTurnId !== _reportableTurnId;
  let turnId = preferActive ? activeTurnId : _reportableTurnId;
  if (!turnId) turnId = activeTurnId;
  const bucket = turnId ? _turnDiagBuckets.get(turnId) : null;
  if (!bucket) {
    return {
      turn_id: turnId,
      listening_epoch: null,
      retained_from_acquisition: false,
      selection_reason: preferActive
        ? "active_stuck_turn"
        : (_reportableTurnId ? "last_submitted_voice_turn" : "active_voice_turn"),
      events: [],
    };
  }
  const cutoff = Date.now() - Math.max(0, Number(maxAgeMs) || 0);
  const events = bucket.events.filter((entry) => entry.ts >= cutoff);
  return {
    turn_id: bucket.turn_id,
    listening_epoch: bucket.listening_epoch,
    retained_from_acquisition: events.some(
      (entry) => entry.evt === "mic:acquire_requested"),
    selection_reason: preferActive
      ? "active_stuck_turn"
      : (bucket.turn_id === _reportableTurnId
          ? "last_submitted_voice_turn" : "active_voice_turn"),
    first_event_at: events[0]?.wall_clock || null,
    last_event_at: events.at(-1)?.wall_clock || null,
    events,
  };
};
// PO 2026-08-07 diagnostic bounded round · expose _pushDiag so
// sibling Voice modules (e.g. leviWakeLock) can emit lifecycle
// events into the SAME ring buffer read by "Copy Full Diagnostics".
// This is diagnostic-only and does not alter any speech, TTS, VAD,
// microphone, wake-lock, or conversation behavior.
export const pushDiag = _pushDiag;

// ---------- Preference (robust to legacy/malformed values) ----------
export const isTtsEnabled = () => {
  try {
    const v = localStorage.getItem(PREF_KEY);
    // Anything other than the literal string "0" is treated as
    // enabled — missing, null, undefined, "", "1", "true", legacy
    // garbage all default to ON so a first-time contractor always
    // hears Levi.
    return v !== "0";
  } catch {
    return true;
  }
};
export const setTtsEnabled = (on) => {
  try {
    localStorage.setItem(PREF_KEY, on ? "1" : "0");
    _pushDiag("pref:set", { on: !!on });
  } catch { /* noop */ }
};
export const readPrefRaw = () => {
  try { return localStorage.getItem(PREF_KEY); } catch { return null; }
};

// ---------- Shared recording admission ----------
// Every recording exit uses this same three-way decision. Known silence is
// suppressed; unavailable acoustic evidence fails open so a real contractor
// turn is never discarded merely because WebAudio could not observe it.
export const decideAcousticAdmission = ({
  acousticEvidenceAvailable,
  voiceEver,
} = {}) => {
  if (acousticEvidenceAvailable !== true) {
    return { kind: "acoustic_evidence_unavailable", shouldSubmit: true };
  }
  if (voiceEver === true) {
    return { kind: "voice_detected", shouldSubmit: true };
  }
  return { kind: "known_no_voice", shouldSubmit: false };
};

// ---------- Curated voice picker (unchanged) ----------
// Curated preferred voices used by ``pickEnglishVoice`` below. Order
// matters — earlier entries win. The list is intentionally scoped to
// browser-TTS fallback voices; server-side Custom Levi Voice, once
// activated separately, remains authoritative. Extended 2026-08-06
// per PO Decision P-1 to recognize male voices shipped on current
// iOS and Android browser/system inventories. All prior macOS and
// Windows selections are preserved verbatim; new entries are additive.
const EN_US_MALE_NAMES = [
  // macOS (preserved from Slice LEVI-VOICE-01).
  "Alex", "Aaron", "Fred", "Nathan", "Tom", "Bruce",
  // Windows (preserved).
  "David", "Mark", "Guy",
  // Cross-platform Google voice-name aliases (preserved).
  "Google US English Male", "Google UK English Male",
  // Chromium en-us voice codes (preserved — apply to desktop Chrome
  // and Android Chrome equally).
  "en-us-x-iom", "en-us-x-tpc", "en-us-x-tpd", "en-us-x-tpf",
  // Additional Windows male names (preserved).
  "Christopher", "Eric", "Roger", "Steffan", "Brandon",
  // P-1 (PO 2026-08-06) · iOS Safari male voices. iOS 15+ ships
  // Aaron (already above), Daniel (en-GB male), and iOS 17.4+
  // ships Reed and Rocko. Rishi is an en-IN male voice available
  // in the "Voice for Levi" enhanced voices download. Junior and
  // Grandpa are older male-timbre iOS voices.
  "Daniel", "Reed", "Rocko", "Rishi", "Junior", "Grandpa",
  // P-1 · Android Chrome male voice codes (Google TTS engine).
  // ``en-us-x-tpc/tpd/tpf`` already above are the primary male
  // codes on Android; adding the "-local" and "-network" forms
  // observed on real devices for defensive matching.
  "en-us-x-tpc-local", "en-us-x-tpd-local", "en-us-x-tpf-local",
  "en-us-x-tpc-network", "en-us-x-tpd-network", "en-us-x-tpf-network",
  // P-1 · Edge (Chromium) additional male natural voices commonly
  // installed on Windows 11 language packs.
  "Andrew", "Brian", "Christopher (Enhanced)", "Guy (Enhanced)",
  "Aria", // NOTE: female — intentionally NOT in this list; kept as
          // comment marker to prevent accidental future additions.
].filter((n) => n !== "Aria");
const EN_EXCLUDE = new Set([
  "Bad News", "Bahh", "Bells", "Boing", "Bubbles", "Cellos", "Deranged",
  "Good News", "Hysterical", "Pipe Organ", "Trinoids", "Whisper", "Zarvox",
  "Albert", "Junior", "Ralph", "Kathy",
]);
const ES_PREFERRED_NAMES = [
  "Jorge", "Diego", "Juan", "Paulina", "Monica", "Marisol",
  "Google español", "Google español de Estados Unidos",
  "Microsoft Sabina", "Microsoft Raul", "Microsoft Pablo",
  "Microsoft Jorge", "Microsoft Dalia", "Microsoft Alonso",
];
const _byName = (voices, wanted) => {
  const target = wanted.toLowerCase();
  return voices.find((v) => (v.name || "").toLowerCase().includes(target));
};
const pickEnglishVoice = (voices) => {
  const enVoices = voices.filter(
    (v) => (v.lang || "").toLowerCase().startsWith("en") && !EN_EXCLUDE.has(v.name)
  );
  if (!enVoices.length) return null;
  for (const name of EN_US_MALE_NAMES) {
    const hit = enVoices.find(
      (v) => (v.lang || "").toLowerCase() === "en-us" &&
             (v.name || "").toLowerCase().includes(name.toLowerCase())
    );
    if (hit) return hit;
  }
  return enVoices.find((v) => (v.lang || "").toLowerCase() === "en-us" && v.localService)
      || enVoices.find((v) => (v.lang || "").toLowerCase() === "en-us")
      || enVoices.find((v) => v.localService)
      || enVoices[0];
};
const pickSpanishVoice = (voices) => {
  const esVoices = voices.filter((v) => (v.lang || "").toLowerCase().startsWith("es"));
  if (!esVoices.length) return null;
  for (const name of ES_PREFERRED_NAMES) {
    const hit = _byName(esVoices, name);
    if (hit) return hit;
  }
  for (const lang of ["es-us", "es-419", "es-mx", "es-la", "es-co", "es-ar"]) {
    const hit = esVoices.find((v) => (v.lang || "").toLowerCase() === lang);
    if (hit) return hit;
  }
  return esVoices.find((v) => v.localService) || esVoices[0];
};

// ---------- Async voice enumeration cache ----------
let _voicesCache = [];
const _refreshVoices = () => {
  try {
    _voicesCache = (window.speechSynthesis && window.speechSynthesis.getVoices()) || [];
  } catch { _voicesCache = []; }
};
if (typeof window !== "undefined" && window.speechSynthesis) {
  _refreshVoices();
  const onChange = () => { _refreshVoices(); _pushDiag("voiceschanged", { count: _voicesCache.length }); };
  try { window.speechSynthesis.addEventListener("voiceschanged", onChange); }
  catch { try { window.speechSynthesis.onvoiceschanged = onChange; } catch { /* noop */ } }
}
export const pickVoice = (lang) => {
  if (!_voicesCache.length) _refreshVoices();
  // Slice LEVI-VOICE-01 · consult the semantic preference model +
  // ranker first. Falls through to the legacy curated pickers if no
  // ranked voice is available (defensive fallback preserves the LV-02
  // behavior for tests and edge devices).
  let chosen = null;
  let source = "legacy_curated";
  try {
    // Dynamic requires break test-time module cycles; static imports
    // are fine here at runtime.
    // eslint-disable-next-line global-require
    const { readVoicePrefs, setPreferredVoiceUri, getPreferredVoiceUri } = require("./leviVoicePrefs");
    // eslint-disable-next-line global-require
    const { chooseVoice } = require("./leviVoiceRanking");
    const prefs = readVoicePrefs(lang || "en");
    // Try the cached preferred voice first, but only if it is still
    // available on the device.
    const cachedUri = getPreferredVoiceUri();
    if (cachedUri) {
      const cached = _voicesCache.find(
        (v) => (v.voiceURI && v.voiceURI === cachedUri) ||
               (v.name && v.name === cachedUri)
      );
      if (cached && (cached.lang || "").toLowerCase().startsWith(prefs.language)) {
        chosen = cached;
        source = "prefs_cached_uri";
      }
    }
    if (!chosen) {
      chosen = chooseVoice(_voicesCache, prefs);
      if (chosen) {
        source = "prefs_ranked";
        // Cache the chosen voice URI so subsequent speaks are stable
        // (avoids re-ranking churn when the voice list is unchanged).
        setPreferredVoiceUri(chosen.voiceURI || chosen.name || null);
      }
    }
  } catch (_) {
    chosen = null;
  }
  if (!chosen) {
    chosen = (lang === "es" ? pickSpanishVoice : pickEnglishVoice)(_voicesCache);
    source = "legacy_curated";
  }
  const final = chosen || _voicesCache[0] || null;
  _pushDiag("pickVoice", {
    lang,
    chosen: chosen ? { name: chosen.name, lang: chosen.lang, local: chosen.localService } : null,
    source,
    fallback_used: !!(final && !chosen),
    voices_available: _voicesCache.length,
  });
  return final;
};

// ---------- TTS priming (iOS user-gesture unlock) ----------
//
// iOS Safari requires the FIRST speechSynthesis.speak() call in a tab
// to occur synchronously within a user gesture. Because Levi speaks in
// response to an async server reply, the gesture is stale by the time
// we call speak(). This primer runs INSIDE the user-gesture stack (mic
// tap, keyboard submit, dev-only test button) and unlocks the audio
// session for the rest of the tab's lifetime. It is a silent
// zero-volume utterance so no audible artifact leaks.
let _primed = false;
export const primeTts = (label = "unspecified") => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    _pushDiag("prime:noop:no-synth"); return false;
  }
  if (_primed) { _pushDiag("prime:already", { label }); return true; }
  try {
    const p = new SpeechSynthesisUtterance(" ");
    p.volume = 0;
    p.rate = 1.0;
    p.pitch = 1.0;
    p.onstart = () => _pushDiag("prime:onstart");
    p.onend = () => _pushDiag("prime:onend");
    p.onerror = (e) => _pushDiag("prime:onerror", { code: e?.error });
    window.speechSynthesis.speak(p);
    _primed = true;
    _pushDiag("prime:invoked", { label, speaking: window.speechSynthesis.speaking, pending: window.speechSynthesis.pending });
    return true;
  } catch (err) {
    _pushDiag("prime:threw", { err: String(err), label });
    return false;
  }
};
export const isPrimed = () => _primed;

// ---------- C-2 · Speaking-state render bridge (rendering only) ----------
//
// Publish/subscribe channel so React consumers (LeviCommandConsole via
// useLeviCommand) can re-render the presence badge at real-time
// speech-start and speech-end boundaries. THIS IS A RENDERING-ONLY
// BRIDGE. It does NOT change WHEN Levi speaks, WHEN he stops, WHETHER
// the microphone re-arms, WHETHER a conversation continues, or WHETHER
// a conversation ends. `isSpeaking()` remains the authoritative source
// of truth for presence derivation; this bridge only guarantees that
// the React tree re-evaluates it at the correct moments.
//
// The publisher fires exclusively at existing lifecycle boundaries
// (browser TTS onstart / final onend / onerror; server audio onplay /
// onended / onpause; cancelActiveSpeech; stopSpeaking). It does not
// introduce any new state machine.
const _speakingSubs = new Set();
let _lastPublishedSpeaking = false;
const _publishSpeaking = (isNow) => {
  const b = !!isNow;
  if (b === _lastPublishedSpeaking) return;
  _lastPublishedSpeaking = b;
  _pushDiag("speaking:publish", { speaking: b });
  for (const cb of _speakingSubs) {
    try { cb(b); } catch { /* noop */ }
  }
};
export const subscribeSpeakingChange = (cb) => {
  if (typeof cb !== "function") return () => {};
  _speakingSubs.add(cb);
  // Fire once with the current state so subscribers hydrate at mount.
  try { cb(_lastPublishedSpeaking); } catch { /* noop */ }
  return () => { _speakingSubs.delete(cb); };
};

// ---------- C-1 · Chromium long-speech reliability helpers ----------
//
// Chromium (desktop + Chromium-based mobile) has a well-known bug
// where speechSynthesis.speak() silently stops firing audio events
// after ~15 seconds of a single utterance. Two mitigations are
// applied — both isolated to browser-TTS playback reliability and
// isolated to this module:
//
//   1. Long replies are split into sentence-boundary chunks and
//      queued sequentially through the existing speechSynthesis
//      queue. The caller-provided onEnd fires EXACTLY ONCE — when
//      the last chunk ends OR on any error / cancel / stale token
//      (identical contract to the pre-C-1 single-utterance path).
//
//   2. While a chunk is playing on a Chromium user-agent, a gentle
//      pause() / resume() keep-alive interval runs every 10 s. This
//      is a no-op on Safari and Firefox.
//
// Neither mitigation changes conversation state, microphone timing,
// end-conversation behavior, fallback selection, or the onEnd
// contract observed by useLeviCommand.js `_onSpokenEnd`.
const _CHUNK_MAX_CHARS = 180;
const _isChromiumLike = () => {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    // Chromium user-agents include "Chrome/". Firefox includes
    // "Firefox/" and does not need chunking. Safari includes
    // "Safari/" without "Chrome/" and does not need chunking.
    return /Chrome\//.test(ua) && !/Firefox\//.test(ua);
  } catch { return false; }
};
const _splitIntoChunks = (text) => {
  const clean = String(text || "").trim();
  if (clean.length <= _CHUNK_MAX_CHARS) return [clean];
  // Sentence-boundary split first (preserves EN/ES punctuation and
  // the Spanish inverted question / exclamation marks live inside
  // the sentence body — the regex only splits AFTER terminal marks).
  const sentences = clean.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g)
                     || [clean];
  const out = [];
  let buf = "";
  for (const s of sentences) {
    const piece = s.trim();
    if (!piece) continue;
    const candidate = buf ? `${buf} ${piece}` : piece;
    if (candidate.length <= _CHUNK_MAX_CHARS) {
      buf = candidate;
    } else {
      if (buf) out.push(buf);
      if (piece.length <= _CHUNK_MAX_CHARS) {
        buf = piece;
      } else {
        // Rare pathological single sentence exceeding the cap.
        // Hard-split on word boundaries so we never send a single
        // utterance longer than the cap.
        const words = piece.split(/\s+/);
        let inner = "";
        for (const w of words) {
          const c2 = inner ? `${inner} ${w}` : w;
          if (c2.length <= _CHUNK_MAX_CHARS) {
            inner = c2;
          } else {
            if (inner) out.push(inner);
            inner = w;
          }
        }
        buf = inner;
      }
    }
  }
  if (buf) out.push(buf);
  return out;
};

// ---------- Speak / stop ----------
let _currentUtter = null; // keep a stable reference to defeat GC
let _lastSpeakToken = 0;
// Phase 7-C · track the caller-registered onEnd binding for the
// currently-playing server-TTS reply so cancelActiveSpeech() can
// notify the caller exactly once. Reset on every playback terminal
// path (ended / error / cancel).
let _currentServerAudioOnEnd = null;
// Cloudflare-origin defense (PO 2026-08-04 QA). Sticky flag: once
// the server TTS route has returned an infrastructure-level
// failure this tab session (503, timeout, network error), we
// short-circuit future calls straight to browser speechSynthesis
// so we don't keep round-tripping through the edge on a doomed
// endpoint. Cleared on reload.
let _serverTtsSessionUnavailable = false;

// ---------- Persistent server-TTS playback owner (Safari unlock) ----------
//
// PO 2026-02 · Brother-approved P0 Safari repair. The professional
// server-TTS route must play its asynchronously fetched MP3 through
// the SAME HTMLAudioElement that was legitimately unlocked inside
// an existing user gesture (mic tap / Stop & Send). Safari rejects
// audio.play() invoked after `await fetch(...)` unless the element
// itself was primed synchronously within a user-gesture stack.
//
// This owner is a module-scoped singleton whose lifetime follows
// the Levi speech layer, NOT any individual reply:
//   Created:   lazily on first primeServerTtsAudio(label) — or
//              on first playServerTtsForReply when primer never ran
//              (unprimed fallback; iOS will typically fall through
//              to browser TTS via play() rejection, unchanged from
//              historical behavior).
//   Survives:  every between-turn cancellation (stopSpeaking,
//              cancelActiveSpeech, submit, startListening, barge-in,
//              endConversation, pauseConversation, status
//              transitions, QA Stop button). The Safari unlock on
//              the element itself is preserved so subsequent replies
//              play immediately.
//   Destroyed: ONLY via disposePersistentPlayback(reason) which is
//              wired to three call sites — hook unmount, contractor
//              toggles Levi voice OFF, contractor toggles TTS OFF.
//
// A monotonic generation counter guards against late-arriving
// fetch responses: any cancel bumps the counter, and each
// playServerTtsForReply captures the current generation in-closure
// at fetch start. A response whose captured generation no longer
// matches _serverTtsGeneration is discarded — the blob is revoked
// and never attached to the element.
const _PERSISTENT_PRIMER_SAMPLE_RATE = 16000;
const _PERSISTENT_PRIMER_CHANNELS = 1;
const _PERSISTENT_PRIMER_BITS_PER_SAMPLE = 8;
const _PERSISTENT_PRIMER_DURATION_MS = 250;
const _PERSISTENT_PRIMER_FRAME_COUNT = Math.round(
  _PERSISTENT_PRIMER_SAMPLE_RATE * (_PERSISTENT_PRIMER_DURATION_MS / 1000),
);
const _createPersistentPrimerWav = () => {
  const blockAlign = _PERSISTENT_PRIMER_CHANNELS
    * (_PERSISTENT_PRIMER_BITS_PER_SAMPLE / 8);
  const byteRate = _PERSISTENT_PRIMER_SAMPLE_RATE * blockAlign;
  const dataSize = _PERSISTENT_PRIMER_FRAME_COUNT * blockAlign;
  const bytes = new Uint8Array(44 + dataSize);
  const view = new DataView(bytes.buffer);
  const ascii = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) {
      bytes[offset + i] = value.charCodeAt(i);
    }
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, _PERSISTENT_PRIMER_CHANNELS, true);
  view.setUint32(24, _PERSISTENT_PRIMER_SAMPLE_RATE, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, _PERSISTENT_PRIMER_BITS_PER_SAMPLE, true);
  ascii(36, "data");
  view.setUint32(40, dataSize, true);
  bytes.fill(128, 44);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:audio/wav;base64,${btoa(binary)}`;
};
const _SILENT_UNLOCK_WAV = _createPersistentPrimerWav();
let _persistentAudio = null;
let _persistentAudioUnlocked = false;
let _persistentAudioUnlockError = null;
let _persistentAudioUnlockInFlight = false;
let _persistentAudioUnlockPromise = null;
let _persistentAudioUnlockAttempt = 0;
let _currentPersistentBlobUrl = null;
let _serverTtsGeneration = 0;

const _nextSpeechTurn = (source, text, mode) => {
  const id = (typeof window !== "undefined")
    ? ((window.__leviSpeechTurnCounter = (window.__leviSpeechTurnCounter || 0) + 1))
    : 1;
  try {
    if (typeof window !== "undefined") {
      window.__leviLastSpeechTurn = {
        id,
        source,
        mode: mode || "unknown",
        reply_len: (text || "").length,
        requested_at: Date.now(),
      };
    }
  } catch { /* noop */ }
  return id;
};

const _bumpServerTtsGeneration = (reason) => {
  _serverTtsGeneration += 1;
  _pushDiag("serverTts:generation_bump", {
    generation: _serverTtsGeneration, reason: reason || "unknown",
  });
  // PO 2026-02 · Brother-approved watchdog delta. Any generation
  // bump means whatever playback the watchdog was guarding is now
  // stale — clear the watchdog immediately so it cannot fire a
  // late trip against a superseded turn.
  try {
    if (typeof _activeWatchdogCleanup === "function") {
      _activeWatchdogCleanup("generation_bump");
    }
  } catch { /* noop */ }
  _activeWatchdogCleanup = null;
};

// Holds the cleanup function for the current turn's watchdog.
// Replaced when a new watchdog is armed; cleared on natural end /
// error / cancel / supersession / dispose.
let _activeWatchdogCleanup = null;

const _getPersistentAudio = () => {
  if (_persistentAudio) return _persistentAudio;
  if (typeof Audio === "undefined") return null;
  try {
    _persistentAudio = new Audio();
    try { _persistentAudio.preload = "auto"; } catch { /* noop */ }
    _pushDiag("persistent:created", {});
  } catch (e) {
    _pushDiag("persistent:create_error", { err: String(e) });
    _persistentAudio = null;
  }
  return _persistentAudio;
};

const _revokeCurrentBlobUrl = () => {
  if (_currentPersistentBlobUrl) {
    try { URL.revokeObjectURL(_currentPersistentBlobUrl); } catch { /* noop */ }
    _currentPersistentBlobUrl = null;
  }
};

const _detachAudioHandlers = (a) => {
  if (!a) return;
  try { a.onplaying = null; } catch { /* noop */ }
  try { a.onended = null; } catch { /* noop */ }
  try { a.onerror = null; } catch { /* noop */ }
  try { a.onpause = null; } catch { /* noop */ }
  try { a.onplay = null; } catch { /* noop */ }
  try { a.ontimeupdate = null; } catch { /* noop */ }
  try { a.onwaiting = null; } catch { /* noop */ }
  try { a.onstalled = null; } catch { /* noop */ }
  try { a.onsuspend = null; } catch { /* noop */ }
};

// PO 2026-02 · Brother-approved Safari stuck-Speaking watchdog.
// Thresholds are picked to never cut off legitimate playback:
//   PROGRESS_STALL_MS — ~5× Safari's typical timeupdate cadence.
//   PAUSE_STALL_MS   — long enough to let real user-invoked pauses
//                      resume without a false trip, short enough
//                      to keep the demo unblocked.
//   ABSOLUTE_MIN_MS  — the shortest "generously long Levi reply"
//                      when duration is not readable from the MP3.
//   ABSOLUTE_PAD_MS  — added to a known duration for jitter and
//                      end-of-file decode latency.
//   TICK_MS          — watchdog polling cadence.
const WATCHDOG_PROGRESS_STALL_MS = 4000;
const WATCHDOG_PAUSE_STALL_MS = 3000;
const WATCHDOG_ABSOLUTE_MIN_MS = 60000;
const WATCHDOG_ABSOLUTE_PAD_MS = 8000;
const WATCHDOG_TICK_MS = 1000;
// SDR-202609-3E8F minimal repair: single guarded fallback retry if no
// measurable progress lands within this window (well inside the 4s
// progress watchdog above).
const FALLBACK_RETRY_MS = 1200;
// Physical iPhone SDR-202609-93B1: a user-gesture primer that had not
// settled within its first second remained pending for another minute until
// page-hide forced AbortError. Keep this deadline strictly primer-scoped;
// response generation, TTS fetch, professional playback, and the playback
// watchdog retain their existing independent lifecycles.
const PERSISTENT_PRIMER_SETTLE_MS = 1000;

const _now = () => {
  try {
    if (typeof performance !== "undefined"
        && typeof performance.now === "function") {
      return performance.now();
    }
  } catch { /* noop */ }
  return Date.now();
};

/**
 * primeServerTtsAudio — Safari HTMLAudioElement unlock.
 *
 * MUST be called synchronously inside an existing legitimate user
 * gesture (mic tap, Stop & Send button). Idempotent — a second call
 * after successful unlock returns true immediately.
 *
 * The embedded 250 ms WAV is 16 kHz mono unsigned 8-bit PCM. Its 4,000
 * real audio frames contain the correct 0x80 silence sample, so the owner
 * remains at normal volume while the primer itself is inaudible.
 */
export const primeServerTtsAudio = (label = "unspecified") => {
  const a = _getPersistentAudio();
  if (!a) {
    _pushDiag("persistent:prime_noop", { reason: "no_audio_ctor", label });
    return false;
  }
  if (_persistentAudioUnlocked) {
    _pushDiag("persistent:prime_already", { label });
    return true;
  }
  if (_persistentAudioUnlockInFlight) {
    _pushDiag("persistent:prime_in_flight", { label });
    return true;
  }
  try {
    _detachAudioHandlers(a);
    a.src = _SILENT_UNLOCK_WAV;
    try { a.muted = false; } catch { /* noop */ }
    try { a.volume = 1.0; } catch { /* noop */ }
    try { a.load(); } catch { /* noop */ }
    _persistentAudioUnlockInFlight = true;
    const attempt = ++_persistentAudioUnlockAttempt;
    let progressResolved = false;
    let resolveProgress = null;
    const progress = new Promise((resolve) => { resolveProgress = resolve; });
    const observeProgress = () => {
      if (progressResolved) return;
      let currentTime = 0;
      let duration = null;
      try { currentTime = Number(a.currentTime) || 0; } catch { currentTime = 0; }
      try { duration = Number.isFinite(a.duration) ? a.duration : null; } catch { duration = null; }
      if (currentTime > 0) {
        progressResolved = true;
        resolveProgress({ kind: "progress", currentTime, duration });
      }
    };
    a.onplaying = observeProgress;
    a.ontimeupdate = observeProgress;
    a.onended = observeProgress;
    const p = a.play();
    {
      const playFailure = (p && typeof p.then === "function")
        ? Promise.resolve(p).then(
          () => new Promise(() => {}),
          (err) => ({ kind: "rejected", err }),
        )
        : new Promise(() => {});
      let timeoutId = null;
      const deadline = new Promise((resolve) => {
        timeoutId = setTimeout(
          () => resolve({ kind: "timeout" }),
          PERSISTENT_PRIMER_SETTLE_MS,
        );
      });
      let managedPromise = null;
      managedPromise = Promise.race([progress, playFailure, deadline]).then((outcome) => {
        if (timeoutId) {
          try { clearTimeout(timeoutId); } catch { /* noop */ }
          timeoutId = null;
        }
        if (a.onplaying === observeProgress) a.onplaying = null;
        if (a.ontimeupdate === observeProgress) a.ontimeupdate = null;
        if (a.onended === observeProgress) a.onended = null;
        if (attempt !== _persistentAudioUnlockAttempt || a !== _persistentAudio) {
          _pushDiag("persistent:unlock_stale", { label, attempt });
          return false;
        }
        _persistentAudioUnlockInFlight = false;
        if (_persistentAudioUnlockPromise === managedPromise) {
          _persistentAudioUnlockPromise = null;
        }
        if (outcome.kind === "timeout") {
          // Retire this primer token before touching the shared element. A
          // subsequent late resolve/reject from Safari is consumed by
          // playFailure but cannot pass the token check or affect the
          // professional source attached later.
          _persistentAudioUnlockAttempt += 1;
          if (a.src === _SILENT_UNLOCK_WAV) {
            try { a.pause(); } catch { /* noop */ }
            try { a.currentTime = 0; } catch { /* noop */ }
            try { a.src = ""; } catch { /* noop */ }
          }
          try { a.volume = 1.0; } catch { /* noop */ }
          _persistentAudioUnlocked = false;
          _persistentAudioUnlockError = "TimeoutError";
          _pushDiag("persistent:unlock_timeout", {
            label, attempt, deadline_ms: PERSISTENT_PRIMER_SETTLE_MS,
          });
          return false;
        }
        if (outcome.kind === "rejected") {
          _persistentAudioUnlocked = false;
          _persistentAudioUnlockError = outcome.err
            ? String(outcome.err.name || outcome.err.message || outcome.err)
            : "rejected";
          try { a.volume = 1.0; } catch { /* noop */ }
          _pushDiag("persistent:unlock_rejected", {
            label, err: _persistentAudioUnlockError,
          });
          return false;
        }
        // A resolved play() promise or onplaying event is not authorization.
        // Only measurable currentTime progress reaches this branch.
        if (a.src === _SILENT_UNLOCK_WAV) {
          try { a.pause(); } catch { /* noop */ }
          try { a.currentTime = 0; } catch { /* noop */ }
          try { a.src = ""; } catch { /* noop */ }
        }
        try { a.volume = 1.0; } catch { /* noop */ }
        _persistentAudioUnlocked = true;
        _persistentAudioUnlockError = null;
        _pushDiag("persistent:unlocked", {
          label,
          current_time: outcome.currentTime,
          duration: outcome.duration,
        });
        return true;
      });
      _persistentAudioUnlockPromise = managedPromise;
    }
    return true;
  } catch (err) {
    _persistentAudioUnlockInFlight = false;
    _persistentAudioUnlockPromise = null;
    _persistentAudioUnlockError = String(err);
    _pushDiag("persistent:prime_threw", { label, err: String(err) });
    return false;
  }
};

/**
 * disposePersistentPlayback — the ONLY function that destroys the
 * persistent playback owner. Wired to exactly three call sites:
 *   1. useLeviCommand.js hook unmount (drawer close / route change / unload).
 *   2. LeviVoiceSettings.jsx — contractor toggles Levi voice OFF.
 *   3. LeviCommandConsole.jsx — contractor toggles TTS OFF.
 *
 * Every other cancellation path (stopSpeaking / cancelActiveSpeech /
 * submit / startListening / barge-in / endConversation /
 * pauseConversation / status-listening transitions / QA Stop) is a
 * playback-cancellation only and MUST NOT destroy the element,
 * otherwise the Safari unlock is lost.
 */
export const disposePersistentPlayback = (reason = "unspecified") => {
  const a = _persistentAudio;
  _bumpServerTtsGeneration("dispose_" + (reason || "unspecified"));
  _revokeCurrentBlobUrl();
  if (a) {
    try { a.pause(); } catch { /* noop */ }
    try { a.currentTime = 0; } catch { /* noop */ }
    _detachAudioHandlers(a);
    try { a.src = ""; } catch { /* noop */ }
    try { a.removeAttribute && a.removeAttribute("src"); } catch { /* noop */ }
    try { a.load && a.load(); } catch { /* noop */ }
  }
  _persistentAudio = null;
  _persistentAudioUnlockAttempt += 1;
  _persistentAudioUnlocked = false;
  _persistentAudioUnlockInFlight = false;
  _persistentAudioUnlockPromise = null;
  _persistentAudioUnlockError = null;
  _currentServerAudioOnEnd = null;
  // PO 2026-02 · Brother-approved watchdog delta. Owner-teardown
  // must explicitly clear Speaking so a stuck-true state cannot
  // survive a toggle-off / unmount.
  _publishSpeaking(false);
  _pushDiag("persistent:disposed", { reason });
};

// Public helper for Package 7-C barge-in. Safe to call repeatedly.
// Stops browser TTS AND pauses the persistent server-TTS element
// (without destroying it), and fires the caller-registered onEnd
// exactly once per utterance with the supplied reason.
export const cancelActiveSpeech = (reason = "cancelled") => {
  let acted = false;
  // PO 2026-02 · ALWAYS bump generation on cancel so any in-flight
  // fetch (whose captured generation predates this call) is
  // invalidated and its response discarded on arrival. This holds
  // even when the persistent element is not yet playing — e.g. a
  // cancel that lands between fetch-start and blob-arrival.
  _bumpServerTtsGeneration("cancel_" + (reason || "unspecified"));
  // Server-TTS side — pause the PERSISTENT element but do NOT
  // destroy it. The Safari unlock persists on the element itself,
  // so the next reply plays without a new gesture.
  const a = _persistentAudio;
  const audioOnEnd = _currentServerAudioOnEnd;
  const hadActivePlayback = !!(a && ((!a.paused && !a.ended) || _currentPersistentBlobUrl));
  if (hadActivePlayback) {
    _revokeCurrentBlobUrl();
    try { a.pause(); } catch { /* noop */ }
    try { a.currentTime = 0; } catch { /* noop */ }
    // Detach handlers so a late fetch resolver's late attach cannot
    // re-fire an old reply's onEnd against the superseded call.
    _detachAudioHandlers(a);
    try { a.src = ""; } catch { /* noop */ }
    _pushDiag("cancelActiveSpeech:server_audio", {
      reason,
      cancellation_owner: "server_audio",
      current_time: (() => { try { return a.currentTime; } catch { return null; } })(),
      duration: (() => { try { return a.duration; } catch { return null; } })(),
      volume: (() => { try { return a.volume; } catch { return null; } })(),
      muted: (() => { try { return a.muted; } catch { return null; } })(),
    });
    try {
      if (typeof audioOnEnd === "function") {
        audioOnEnd({ reason });
      }
    } catch { /* noop */ }
    acted = true;
  }
  _currentServerAudioOnEnd = null;
  // Browser TTS side.
  try {
    if (typeof window !== "undefined"
        && window.speechSynthesis
        && (window.speechSynthesis.speaking
            || window.speechSynthesis.pending)) {
      window.speechSynthesis.cancel();
      _pushDiag("cancelActiveSpeech:browser_tts", {
        reason,
        cancellation_owner: "browser_tts",
      });
      acted = true;
    }
  } catch { /* noop */ }
  // C-2 · render bridge: publish speaking=false when either channel
  // was actually torn down by the cancel. `isSpeaking()` is the
  // authoritative source and will re-evaluate correctly on the next
  // render triggered by this publish.
  if (acted) _publishSpeaking(false);
  return acted;
};

const _actuallySpeak = (text, lang, token, onEnd) => {
  // Phase 7-C · idempotent onEnd wrapper for browser TTS. Cancel
  // fires onerror; natural completion fires onend. Either path
  // must invoke the caller exactly once.
  //
  // C-1 (2026-08-06): the same wrapper now also fires exactly once
  // per CALL rather than once per utterance. When the reply is
  // chunked for Chromium long-speech reliability, only the LAST
  // chunk's onend fires _fireOnce("ended"); intermediate chunks
  // advance the queue without notifying the caller. Errors and
  // stale tokens still terminate the whole call once.
  let _onEndFired = false;
  const _fireOnce = (reason) => {
    if (_onEndFired) return;
    _onEndFired = true;
    try { if (typeof onEnd === "function") onEnd({ reason }); }
    catch { /* noop */ }
  };
  try {
    if (token !== _lastSpeakToken) {
      _pushDiag("speak:stale-token", { token, latest: _lastSpeakToken });
      _fireOnce("stale_token");
      return;
    }
    // Voice + rate + language tag are resolved ONCE for the entire
    // reply so pronunciation stays uniform across chunks (identical
    // to the pre-C-1 single-utterance behavior).
    const v = pickVoice(lang);
    // Slice LEVI-VOICE-01 · read speaking rate from the semantic
    // preference model. Fallback to 1.0 if the model is unavailable.
    let _rate = 1.0;
    try {
      // eslint-disable-next-line global-require
      const { getSpeakingRate } = require("./leviVoicePrefs");
      _rate = getSpeakingRate();
    } catch (_) { _rate = 1.0; }
    const langTag = (v && v.lang) ? v.lang : (lang === "es" ? "es-US" : "en-US");
    // C-1 · chunk long text on Chromium. Non-Chromium browsers take
    // the single-chunk path so their behavior is byte-identical to
    // pre-C-1. Empty / short text also single-chunks.
    const _needsChunking = _isChromiumLike() && String(text).trim().length > _CHUNK_MAX_CHARS;
    const chunks = _needsChunking ? _splitIntoChunks(text) : [String(text)];
    // C-1 · Chromium keep-alive. Every 10 s pause()+resume() to
    // counteract the ~15 s silent-stall bug. Idempotent; disposed
    // by _stopKeepAlive on any terminal path.
    let _keepAliveTimer = null;
    const _startKeepAlive = () => {
      if (_keepAliveTimer) return;
      if (!_isChromiumLike()) return;
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      _keepAliveTimer = setInterval(() => {
        try {
          const ss = window.speechSynthesis;
          if (!ss) return;
          if (ss.speaking && !ss.paused) {
            ss.pause();
            ss.resume();
          }
        } catch { /* noop */ }
      }, 10000);
    };
    const _stopKeepAlive = () => {
      if (_keepAliveTimer) {
        try { clearInterval(_keepAliveTimer); } catch { /* noop */ }
        _keepAliveTimer = null;
      }
    };
    // Sequential chunk pump. Preserves the pre-C-1 diag surface for
    // the FIRST chunk (utterance:start, speak:invoked) and adds
    // per-chunk diag events for observability.
    let _idx = 0;
    const _speakNext = () => {
      if (_onEndFired) return;
      if (token !== _lastSpeakToken) {
        _pushDiag("speak:stale-mid-chunk", { token, latest: _lastSpeakToken, i: _idx });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("stale_mid_chunk");
        return;
      }
      if (_idx >= chunks.length) {
        _pushDiag("utterance:all-chunks-end", { count: chunks.length });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("ended");
        return;
      }
      const seg = chunks[_idx];
      const u = new SpeechSynthesisUtterance(String(seg));
      u.lang = langTag;
      if (v) u.voice = v;
      u.rate = _rate;
      u.pitch = 0.95;
      u.volume = 1.0;
      u.onstart = () => {
        if (_idx === 0) {
          _pushDiag("utterance:start", {
            voice: v?.name || "default", lang: u.lang, textLen: text.length,
            chunks: chunks.length,
            speaking: window.speechSynthesis.speaking,
          });
          _publishSpeaking(true);
        }
        _startKeepAlive();
      };
      u.onend = () => {
        _pushDiag("utterance:end", {
          voice: v?.name || "default",
          i: _idx, total: chunks.length,
        });
        _idx += 1;
        _speakNext();
      };
      u.onerror = (e) => {
        _pushDiag("utterance:error", {
          code: e?.error || "unknown", voice: v?.name || "default",
          i: _idx, total: chunks.length,
        });
        _stopKeepAlive();
        _publishSpeaking(false);
        _fireOnce("error");
      };
      _currentUtter = u; // stable ref, prevents GC before onend/onerror
      window.speechSynthesis.speak(u);
      if (_idx === 0) {
        _pushDiag("speak:invoked", {
          voice: v?.name || "default",
          lang: u.lang,
          textLen: text.length,
          chunks: chunks.length,
          primed: _primed,
          speaking: window.speechSynthesis.speaking,
          pending: window.speechSynthesis.pending,
          paused: window.speechSynthesis.paused,
          voice_available: _voicesCache.length,
        });
      }
    };
    _speakNext();
  } catch (err) {
    _pushDiag("speak:threw", { err: String(err) });
    _publishSpeaking(false);
    _fireOnce("threw");
  }
};

export const speak = (text, lang = "en", opts = {}) => {
  const onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  const _fireEnd = (reason) => {
    try { if (onEnd) onEnd({ reason }); } catch { /* noop */ }
  };
  if (!text || typeof window === "undefined" || !window.speechSynthesis) {
    _pushDiag("speak:noop", {
      textLen: (text || "").length,
      hasSynth: !!(typeof window !== "undefined" && window.speechSynthesis),
    });
    // Phase 7-B · fire onEnd even on no-op so the caller's auto-
    // listen path still runs when TTS is unavailable.
    _fireEnd("noop_no_synth_or_text");
    return;
  }
  if (!isTtsEnabled()) {
    _pushDiag("speak:disabled", { textLen: text.length });
    _fireEnd("noop_tts_disabled");
    return;
  }
  const token = ++_lastSpeakToken;
  const busy = window.speechSynthesis.speaking || window.speechSynthesis.pending;
  if (busy) {
    _pushDiag("cancel:before-speak", { reason: "duck", speaking: window.speechSynthesis.speaking, pending: window.speechSynthesis.pending });
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    // iOS Safari race: after cancel() the queue isn't immediately
    // ready. A 60ms delay is enough to let the engine settle without
    // impacting perceived latency.
    setTimeout(() => _actuallySpeak(text, lang, token, onEnd), 60);
  } else {
    _actuallySpeak(text, lang, token, onEnd);
  }
};

export const stopSpeaking = (reason = "unspecified") => {
  try {
    _pushDiag("stopSpeaking", {
      reason,
      speaking: window.speechSynthesis?.speaking,
      pending: window.speechSynthesis?.pending,
    });
    window.speechSynthesis?.cancel?.();
  } catch { /* noop */ }
  // PO 2026-02 · Persistent Playback Owner. stopSpeaking is a hard
  // stop without notification (distinct from cancelActiveSpeech
  // which fires the caller's onEnd). ALWAYS bump the generation so
  // any in-flight fetch is invalidated on arrival, then pause the
  // persistent element and revoke the current blob URL. The
  // element itself survives — Safari unlock preserved.
  _bumpServerTtsGeneration("stopSpeaking_" + (reason || "unspecified"));
  const a = _persistentAudio;
  if (a && _persistentAudioUnlockInFlight && !_currentPersistentBlobUrl) {
    _pushDiag("persistent:cancel_deferred_for_unlock", { reason });
  } else if (a && ((!a.paused && !a.ended) || _currentPersistentBlobUrl)) {
    _revokeCurrentBlobUrl();
    try { a.pause(); } catch { /* noop */ }
    try { a.currentTime = 0; } catch { /* noop */ }
    _detachAudioHandlers(a);
    try { a.src = ""; } catch { /* noop */ }
  }
  _currentServerAudioOnEnd = null;
  _currentUtter = null;
  // C-2 · render bridge: publish speaking=false defensively. The
  // browser TTS `onerror` path also publishes false, but the cancel
  // may race the event loop and the presence badge should reflect
  // "not speaking" immediately.
  _publishSpeaking(false);
};

export const isSpeaking = () => {
  try {
    // Phase 7-C · isSpeaking must reflect BOTH channels so barge-in
    // detection arms correctly during server-TTS playback (not just
    // browser TTS). Reads the persistent playback owner directly.
    const a = _persistentAudio;
    if (a && !a.paused && !a.ended && a.src) return true;
    return !!window.speechSynthesis?.speaking;
  } catch { return false; }
};

// ---------- Shared Levi-reply entry point ----------
//
// Both the diagnostic "Test Levi Voice" button AND every normal Levi
// conversational reply (from LV-01 voice-intake, LV-02 command
// console, and any future mode) MUST call this function to produce
// spoken output. This guarantees a single canonical speech pipeline
// and gives the Product Owner a single diagnostic event
// ("normalReply:speakRequested") to trace when a normal reply fails to
// speak.
//
// Behavior:
//   - Emits `normalReply:speakRequested` (or `speakLeviReply:test`
//     when opts.source === "test") with reply length, locale,
//     tts_enabled, current mode/status, and any extra ctx fields.
//   - Honors the contractor's voice-off preference for normal replies.
//     The dev "test" source bypasses the preference so the PO can
//     always prove browser audio in isolation.
//   - Never speaks empty text.
//   - Delegates the actual playback to `speak()` — same code path as
//     the diagnostic Test button.
//
export const speakLeviReply = (text, locale = "en", opts = {}) => {
  const source = opts.source || "normal_reply";
  const enabled = isTtsEnabled();
  const eventName = source === "test" ? "speakLeviReply:test" : "normalReply:speakRequested";
  const _onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  const _fireEnd = (reason) => {
    try { if (_onEnd) _onEnd({ reason }); } catch { /* noop */ }
  };
  // P0 REPAIR-2 (PO 2026-08-09) · monotonic speech turn id so a
  // canceled utterance in the diag ring buffer can be attributed to
  // the specific speak-request that canceled it. Also mirrored to
  // window.__leviLastSpeechTurn for Copy Diagnostics correlation.
  const _turnId = _nextSpeechTurn(source, text, opts.mode);
  _pushDiag(eventName, {
    speech_turn_id: _turnId,
    reply_len: (text || "").length,
    locale,
    tts_enabled: enabled,
    source,
    mode: opts.mode || "unknown",
    status: opts.status || "unknown",
    is_listening: !!opts.isListening,
  });
  if (!text || !String(text).trim()) {
    _pushDiag("normalReply:blocked", { reason: "empty_text", source });
    _fireEnd("blocked_empty_text");
    return false;
  }
  if (opts.isListening) {
    _pushDiag("normalReply:blocked", { reason: "listening", source });
    _fireEnd("blocked_listening");
    return false;
  }
  if (!enabled && source !== "test") {
    _pushDiag("normalReply:blocked", { reason: "tts_disabled", source });
    _fireEnd("blocked_tts_disabled");
    return false;
  }
  speak(text, locale, { onEnd: _onEnd });
  return true;
};

// ---------- Phase 7-D · server-TTS branch ----------
// Fetches an MP3 blob from POST /api/levi/tts/reply and plays it via
// the persistent HTMLAudioElement. Professional server TTS is the sole
// normal contractor-facing voice. A pre-start failure leaves the already-
// visible response on screen and completes the turn without changing voice.
//
// Contract:
//   text          reply text as composed by the server (already
//                 style-transformed).
//   locale        "en" or "es".
//   opts.voice_id "professional" | "warm" — required for server path.
//                 When absent, playback completes visibly without audio.
//   opts.style    "brief" | "balanced" | "conversational".
//   opts.speed    "slow" | "normal" | "fast".
//   opts.readAloud When explicitly false, plays nothing (parity with
//                 the server-side `levi_read_aloud_enabled`
//                 preference).
export const playServerTtsForReply = (text, locale = "en", opts = {}) => {
  const enabled = isTtsEnabled();
  const onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  const _turnId = _nextSpeechTurn(
    opts.source || "normal_reply", text, opts.mode || "command");
  const _ttsCorrelationId = `tts-${_turnId}-${Date.now()}`;
  const _requestStartedAt = _now();
  const _ttsDiag = (evt, data = {}) => _pushDiag(evt, {
    speech_turn_id: _turnId,
    tts_correlation_id: _ttsCorrelationId,
    ...(data || {}),
  });
  // Recorder-lifecycle report 2026-08-04 · optional side-channel
  // callback the caller can subscribe to for HONEST audio-drop
  // notices. Fires AT MOST ONCE when professional playback cannot
  // start. The visible response remains authoritative.
  const onFallback = typeof opts.onFallback === "function"
    ? opts.onFallback : null;
  // PO 2026-08-30 · P0 Voice · D1 (late-arm barge-in). Fires AT MOST
  // ONCE when the SERVER-TTS success path actually begins playing
  // through the HTMLAudioElement — i.e. when Chromium's WebRTC AEC
  // has a reference signal it can subtract from the mic. It is
  // deliberately NOT called on the browser-speechSynthesis fallback
  // route (runFallback → speak) because that route bypasses the
  // browser AEC reference on Windows/Edge and self-output can bleed
  // into the mic at levels indistinguishable from human voice.
  const onPlaybackStart = typeof opts.onPlaybackStart === "function"
    ? opts.onPlaybackStart : null;
  let _playbackStartFired = false;
  const _firePlaybackStart = () => {
    if (_playbackStartFired) return;
    _playbackStartFired = true;
    try { if (onPlaybackStart) onPlaybackStart({ source: "server_tts" }); }
    catch { /* noop */ }
  };
  let _fallbackFired = false;
  const _fireFallback = (reason) => {
    if (_fallbackFired) return false;
    _fallbackFired = true;
    try { if (onFallback) onFallback({ reason }); }
    catch { /* noop */ }
    return true;
  };
  // Phase 7-C · idempotent onEnd wrapper. cancelActiveSpeech and
  // the browser-queued 'ended' event can both race for the same
  // utterance; the wrapper guarantees the caller sees exactly one
  // fire per playback session.
  let _onEndFired = false;
  const _fireEnd = (reason) => {
    if (_onEndFired) return;
    _onEndFired = true;
    try { if (onEnd) onEnd({ reason }); } catch { /* noop */ }
  };
  const completeVisibleOnly = (reason) => {
    if (!_fireFallback(reason)) return false;
    _ttsDiag("serverTts:visible_only", { reason });
    _publishSpeaking(false);
    _fireEnd(`professional_unavailable_${reason}`);
    return false;
  };
  // Cloudflare-origin defense (PO 2026-08-04 QA). Once the server
  // TTS route has returned an infrastructure-level failure this
  // tab session (503 tts_unavailable, timeout, or network error),
  // skip the round-trip entirely for subsequent replies and fall
  // straight to browser speechSynthesis. Reduces the surface area
  // for edge/proxy retries that can surface as
  // "origin returned invalid or incomplete response." Cleared on
  // reload.
  if (_serverTtsSessionUnavailable) {
    return completeVisibleOnly("cached_unavailable");
  }
  if (opts.readAloud === false) {
    _ttsDiag("serverTts:blocked", { reason: "read_aloud_disabled" });
    // Phase 7-B · fire onEnd immediately so the caller's auto-listen
    // path still arms when TTS is intentionally silent (contractor
    // has read-aloud disabled). Without this, the loop stalls.
    _fireEnd("blocked_read_aloud_disabled");
    return false;
  }
  if (!text || !String(text).trim()) {
    _ttsDiag("serverTts:blocked", { reason: "empty_text" });
    _fireEnd("blocked_empty_text");
    return false;
  }
  if (!enabled) {
    _ttsDiag("serverTts:blocked", { reason: "tts_disabled" });
    _fireEnd("blocked_tts_disabled");
    return false;
  }
  if (!opts.voice_id) {
    return completeVisibleOnly("missing_voice_id");
  }
  // PO 2026-02 · Capture generation OUTSIDE the try so the outer
  // catch can consult it during a fetch abort/error and still
  // distinguish supersession from genuine failure.
  const _myGeneration = ++_serverTtsGeneration;
  _ttsDiag("serverTts:request_started", {
    generation: _myGeneration, voice_id: opts.voice_id, locale,
    environment: (typeof window !== "undefined" && window.location)
      ? window.location.hostname : null,
  });
  (async () => {
    try {
      const apiBase = (typeof process !== "undefined"
        && process.env
        && process.env.REACT_APP_BACKEND_URL) || "";
      let token = "";
      try { token = localStorage.getItem("jc_token") || ""; } catch { /* noop */ }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const r = await fetch(`${apiBase}/api/levi/tts/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          text: String(text),
          voice_id: opts.voice_id,
          language: locale || "en",
          style: opts.style || "balanced",
          speed: opts.speed || "normal",
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      _ttsDiag("serverTts:response_received", {
        generation: _myGeneration, status: r.status, ok: r.ok,
        elapsed_ms: Math.round(_now() - _requestStartedAt),
        content_type: r.headers?.get?.("content-type") || null,
        content_length: r.headers?.get?.("content-length") || null,
      });
      // Stale-generation guard AFTER fetch. A cancel/supersede while
      // awaiting bumps the counter — discard and DO NOT fall back
      // (falling back would speak superseded content over the new
      // reply's playback, violating the "exactly one owner" invariant).
      if (_myGeneration !== _serverTtsGeneration) {
        _ttsDiag("serverTts:discarded", {
          reason: "stale_generation",
          generation: _myGeneration, current: _serverTtsGeneration,
        });
        _fireEnd("stale_generation");
        return;
      }
      if (!r.ok) {
        completeVisibleOnly(`http_${r.status}`);
        return;
      }
      const blob = await r.blob();
      _ttsDiag("serverTts:blob_ready", {
        generation: _myGeneration,
        byte_count: blob?.size || 0,
        mime_type: blob?.type || null,
        elapsed_ms: Math.round(_now() - _requestStartedAt),
      });
      if (_myGeneration !== _serverTtsGeneration) {
        _ttsDiag("serverTts:discarded", {
          reason: "stale_generation_after_blob",
          generation: _myGeneration, current: _serverTtsGeneration,
        });
        _fireEnd("stale_generation");
        return;
      }
      if (!blob || blob.size < 100) {
        completeVisibleOnly("empty_blob");
        return;
      }
      // If a legitimate gesture primer is still settling, let that play()
      // promise finish before replacing the same element's source. This is
      // the single-owner ordering guarantee required by physical iOS Safari.
      const unlockPromise = _persistentAudioUnlockPromise;
      if (unlockPromise) {
        _ttsDiag("serverTts:waiting_for_unlock", {
          generation: _myGeneration,
        });
        await unlockPromise;
        _ttsDiag("serverTts:unlock_settled", {
          generation: _myGeneration,
          unlocked: _persistentAudioUnlocked,
          unlock_error: _persistentAudioUnlockError,
        });
        if (_myGeneration !== _serverTtsGeneration) {
          _ttsDiag("serverTts:discarded", {
            reason: "stale_generation_after_unlock",
            generation: _myGeneration, current: _serverTtsGeneration,
          });
          _fireEnd("stale_generation");
          return;
        }
      }
      // Acquire the persistent playback owner. Created lazily if
      // primeServerTtsAudio was never invoked (unprimed fallback —
      // Safari will typically reject play() and we'll take the
      // browser-fallback lane, preserving pre-repair behavior).
      const a = _getPersistentAudio();
      if (!a) {
        completeVisibleOnly("no_audio_owner");
        return;
      }
      // Revoke any prior blob URL still attached to the persistent
      // element (defensive; cancel usually already handled this).
      _revokeCurrentBlobUrl();
      const url = URL.createObjectURL(blob);
      _currentPersistentBlobUrl = url;
      _currentServerAudioOnEnd = onEnd;
      // Detach any prior handlers so a superseded reply's late
      // event dispatch cannot fire against this call's element.
      _detachAudioHandlers(a);
      // PO 2026-02 · Brother-approved watchdog delta. Exactly-once
      // completion guard scoped to THIS reply. Any of natural
      // ended / error / watchdog trip / cancel-via-detach may race;
      // this guard ensures onEnd, blob revocation, and generation
      // bookkeeping happen at most once for this turn.
      let _completionFired = false;
      let _watchdogInterval = null;
      let _pauseStartedAt = null;
      let _lastProgressAt = 0;
      let _playbackStartedAt = 0;
      let _lastKnownCurrentTime = 0;
      let _fallbackRetryTimer = null;
      let _fallbackRetryAttempted = false;
      const _clearWatchdog = (reason) => {
        if (_watchdogInterval) {
          try { clearInterval(_watchdogInterval); } catch { /* noop */ }
          _watchdogInterval = null;
          _ttsDiag("serverTts:watchdog_cancel", {
            generation: _myGeneration, reason: reason || "unspecified",
          });
        }
        _pauseStartedAt = null;
      };
      const _clearCurrentAttachment = () => {
        if (_currentPersistentBlobUrl === url) {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
          _currentPersistentBlobUrl = null;
        }
        _currentServerAudioOnEnd = null;
      };
      const _completeOnce = (terminalReason, opts2 = {}) => {
        if (_completionFired) return;
        _completionFired = true;
        if (_fallbackRetryTimer) {
          try { clearTimeout(_fallbackRetryTimer); } catch { /* noop */ }
          _fallbackRetryTimer = null;
        }
        _clearWatchdog(terminalReason);
        if (_activeWatchdogCleanup === _armedWatchdogCleanupRef.current) {
          _activeWatchdogCleanup = null;
        }
        _ttsDiag("serverTts:terminal", {
          generation: _myGeneration, reason: terminalReason,
          currentTime: (() => { try { return a.currentTime; } catch { return null; } })(),
          duration: (() => { try { return a.duration; } catch { return null; } })(),
          paused: (() => { try { return a.paused; } catch { return null; } })(),
          ended: (() => { try { return a.ended; } catch { return null; } })(),
          volume: (() => { try { return a.volume; } catch { return null; } })(),
          muted: (() => { try { return a.muted; } catch { return null; } })(),
          ready_state: (() => { try { return a.readyState; } catch { return null; } })(),
          network_state: (() => { try { return a.networkState; } catch { return null; } })(),
        });
        _clearCurrentAttachment();
        if (opts2.publishSpeakingFalse !== false) {
          _publishSpeaking(false);
        }
        try {
          if (typeof onEnd === "function" && !_onEndFired) {
            _onEndFired = true;
            onEnd({ reason: terminalReason });
          }
        } catch { /* noop */ }
      };
      // The active-watchdog cleanup ref must be a stable object so
      // _bumpServerTtsGeneration can null it back when it belongs
      // to THIS turn without accidentally clearing a NEXT turn's
      // watchdog that has since been armed.
      const _armedWatchdogCleanupRef = { current: null };
      const _tripWatchdog = (reason) => {
        // Stale-generation check first — cannot affect newer turn.
        if (_myGeneration !== _serverTtsGeneration) return;
        if (_completionFired) return;
        _ttsDiag("serverTts:watchdog_trip", {
          generation: _myGeneration, reason,
          currentTime: (() => { try { return a.currentTime; } catch { return null; } })(),
          duration: (() => { try { return a.duration; } catch { return null; } })(),
          paused: (() => { try { return a.paused; } catch { return null; } })(),
          ended: (() => { try { return a.ended; } catch { return null; } })(),
          readyState: (() => { try { return a.readyState; } catch { return null; } })(),
          networkState: (() => { try { return a.networkState; } catch { return null; } })(),
        });
        // Retire this generation so a late natural ended/error from
        // Safari can no longer complete this turn a second time.
        _bumpServerTtsGeneration("watchdog_" + reason);
        // Stop / reset the persistent element (preserving the
        // element itself — Safari unlock survives watchdog recovery).
        try { a.pause(); } catch { /* noop */ }
        try { a.currentTime = 0; } catch { /* noop */ }
        _detachAudioHandlers(a);
        try { a.src = ""; } catch { /* noop */ }
        // Complete once, publish Speaking=false, fire onEnd with
        // the watchdog reason so the normal listening re-arm path
        // takes over. NO fallback to Samantha — the reply text is
        // already displayed in the transcript.
        _completeOnce("watchdog_" + reason);
      };
      const _confirmProfessionalProgress = (evidence) => {
        if (_playbackStartedAt !== 0) return true;
        let currentTime = 0;
        try { currentTime = Number(a.currentTime) || 0; } catch { currentTime = 0; }
        if (currentTime <= 0) return false;
        _playbackStartedAt = _now();
        _lastProgressAt = _playbackStartedAt;
        _lastKnownCurrentTime = currentTime;
        _ttsDiag("serverTts:playback_started", {
          generation: _myGeneration, voice_id: opts.voice_id, locale,
          evidence: evidence || "measurable_progress",
          current_time: currentTime,
          duration: a.duration,
          volume: a.volume,
          muted: a.muted,
          ready_state: a.readyState,
          network_state: a.networkState,
          output_route: typeof a.sinkId === "string" ? a.sinkId : null,
        });
        _publishSpeaking(true);
        _firePlaybackStart();
        return true;
      };
      const _armWatchdog = () => {
        if (_watchdogInterval) return;
        const armedAt = _now();
        _lastProgressAt = armedAt;
        // The existing four-second progress watchdog starts on provisional
        // onplaying so a Safari event with currentTime=0 still terminates.
        _ttsDiag("serverTts:watchdog_arm", {
          generation: _myGeneration,
          progress_stall_ms: WATCHDOG_PROGRESS_STALL_MS,
          pause_stall_ms: WATCHDOG_PAUSE_STALL_MS,
        });
        _armedWatchdogCleanupRef.current = (reason) => {
          _clearWatchdog(reason);
        };
        _activeWatchdogCleanup = _armedWatchdogCleanupRef.current;
        _watchdogInterval = setInterval(() => {
          // Stale-generation short circuit.
          if (_myGeneration !== _serverTtsGeneration) {
            _clearWatchdog("stale_generation_tick");
            return;
          }
          if (_completionFired) {
            _clearWatchdog("completion_already");
            return;
          }
          const nowT = _now();
          let paused, ended, duration, curT;
          try { paused = a.paused; ended = a.ended;
                duration = a.duration; curT = a.currentTime; }
          catch { return; }
          // 1. Natural end already? Let onended handle it; do nothing.
          if (ended) return;
          // 2. Pause-stall check.
          if (paused) {
            if (_pauseStartedAt === null) _pauseStartedAt = nowT;
            const pausedFor = nowT - _pauseStartedAt;
            // Legitimate near-end pause (Safari can pause in the
            // final few ms before firing ended). Give it slack.
            const nearEnd = Number.isFinite(duration)
              && duration > 0
              && curT >= duration - 0.25;
            if (!nearEnd && pausedFor >= WATCHDOG_PAUSE_STALL_MS) {
              _tripWatchdog("pause_stall");
              return;
            }
          } else {
            _pauseStartedAt = null;
          }
          // 3. Progress-stall check (only when supposedly playing).
          if (!paused) {
            // Update _lastKnownCurrentTime bookkeeping — timeupdate
            // updates _lastProgressAt on real progress. If timeupdate
            // hasn't fired but currentTime advanced, still healthy.
            if (curT > _lastKnownCurrentTime + 0.05) {
              _confirmProfessionalProgress("watchdog_current_time");
              _lastKnownCurrentTime = curT;
              _lastProgressAt = nowT;
            }
            const stalledFor = nowT - _lastProgressAt;
            if (stalledFor >= WATCHDOG_PROGRESS_STALL_MS) {
              _tripWatchdog("progress_stall");
              return;
            }
          }
          // 4. Absolute ceiling.
          const durationMs = Number.isFinite(duration) && duration > 0
            ? Math.round(duration * 1000)
            : null;
          const ceilingMs = durationMs !== null
            ? Math.max(durationMs + WATCHDOG_ABSOLUTE_PAD_MS,
                       WATCHDOG_ABSOLUTE_MIN_MS)
            : WATCHDOG_ABSOLUTE_MIN_MS;
          if (nowT - armedAt >= ceilingMs) {
            _tripWatchdog("absolute_ceiling");
          }
        }, WATCHDOG_TICK_MS);
      };
      a.onplaying = () => {
        _ttsDiag("serverTts:media_playing_provisional", {
          generation: _myGeneration, voice_id: opts.voice_id, locale,
          current_time: a.currentTime,
          duration: a.duration,
          volume: a.volume,
          muted: a.muted,
          ready_state: a.readyState,
          network_state: a.networkState,
        });
        _armWatchdog();
        _confirmProfessionalProgress("onplaying_current_time");
      };
      let _lastProgressDiagAt = 0;
      a.ontimeupdate = () => {
        // Progress signal — updates freshness for the watchdog.
        // Diagnostic emission is rate-limited to avoid ring-buffer
        // flooding (Safari fires ~4/s).
        try {
          const t = a.currentTime;
          _armWatchdog();
          _confirmProfessionalProgress("timeupdate");
          if (t > _lastKnownCurrentTime + 0.05) {
            _lastKnownCurrentTime = t;
            _lastProgressAt = _now();
          }
          if (_lastProgressAt - _lastProgressDiagAt >= 1000) {
            _lastProgressDiagAt = _lastProgressAt;
            _ttsDiag("serverTts:playback_progress", {
              generation: _myGeneration,
              current_time: a.currentTime,
              duration: a.duration,
              volume: a.volume,
              muted: a.muted,
              ready_state: a.readyState,
              network_state: a.networkState,
            });
          }
        } catch { /* noop */ }
      };
      a.onwaiting = () => {
        _ttsDiag("serverTts:media_waiting", {
          generation: _myGeneration, current_time: a.currentTime,
          duration: a.duration, volume: a.volume, muted: a.muted,
          ready_state: a.readyState, network_state: a.networkState,
        });
      };
      a.onstalled = () => {
        _ttsDiag("serverTts:media_stalled", {
          generation: _myGeneration, current_time: a.currentTime,
          duration: a.duration, volume: a.volume, muted: a.muted,
          ready_state: a.readyState, network_state: a.networkState,
        });
      };
      a.onsuspend = () => {
        _ttsDiag("serverTts:media_suspend", {
          generation: _myGeneration, current_time: a.currentTime,
          duration: a.duration, volume: a.volume, muted: a.muted,
          ready_state: a.readyState, network_state: a.networkState,
        });
      };
      a.onended = () => {
        _confirmProfessionalProgress("ended_current_time");
        _ttsDiag("serverTts:playback_ended", {
          generation: _myGeneration, current_time: a.currentTime,
          duration: a.duration, volume: a.volume, muted: a.muted,
        });
        _completeOnce("ended");
      };
      a.onerror = () => {
        _ttsDiag("serverTts:audio_error", {
          generation: _myGeneration,
          error_code: (() => { try { return a.error && a.error.code; } catch { return null; } })(),
          current_time: (() => { try { return a.currentTime; } catch { return null; } })(),
          duration: (() => { try { return a.duration; } catch { return null; } })(),
          volume: (() => { try { return a.volume; } catch { return null; } })(),
          muted: (() => { try { return a.muted; } catch { return null; } })(),
          ready_state: (() => { try { return a.readyState; } catch { return null; } })(),
          network_state: (() => { try { return a.networkState; } catch { return null; } })(),
        });
        // Only auto-fallback if playback had not yet started; a
        // decode error mid-play is treated as watchdog terminal
        // (no re-speak with Samantha, per Brother directive).
        if (_playbackStartedAt === 0) {
          _clearWatchdog("audio_error_prestart");
          _clearCurrentAttachment();
          _publishSpeaking(false);
          completeVisibleOnly("audio_error");
          _completionFired = true;  // fallback owns the turn from here
          return;
        }
        _completeOnce("audio_error_midplay");
      };
      a.onpause = () => {
        // Diagnostic — distinguish internal-teardown pause (which
        // sets a flag we could add later) from external pause.
        // Today we can only observe the raw event.
        try {
          _ttsDiag("serverTts:pause_observed", {
            generation: _myGeneration,
            currentTime: a.currentTime,
            duration: a.duration,
            ended: a.ended,
            volume: a.volume,
            muted: a.muted,
            ready_state: a.readyState,
            network_state: a.networkState,
          });
          if (a.ended) _publishSpeaking(false);
        } catch { /* noop */ }
      };
      try {
        // Stop any in-flight browser TTS before attaching server audio
        // to preserve the "exactly one active speech owner" invariant.
        if (typeof window !== "undefined" && window.speechSynthesis) {
          try { window.speechSynthesis.cancel(); } catch { /* noop */ }
        }
        // Source swap only — do NOT call a.load() here. On physical
        // iOS Safari an explicit load() after swapping .src resets the
        // element's network/ready state and drops the gesture unlock
        // granted by primeServerTtsAudio, even though this is the same
        // HTMLAudioElement. That reload was the SDR-202609-3E8F root
        // cause; the element is reused with a source swap only now.
        a.src = url;
        _ttsDiag("serverTts:play_requested", {
          generation: _myGeneration,
          unlocked: _persistentAudioUnlocked,
          unlock_error: _persistentAudioUnlockError,
          volume: a.volume,
          muted: a.muted,
          ready_state: a.readyState,
          network_state: a.networkState,
        });
        const p = a.play();
        if (p && typeof p.catch === "function") {
          p.catch(() => {
            // If we were superseded during the play() microtask,
            // do not treat it as an autoplay failure — the cancel
            // already invalidated this generation.
            if (_myGeneration !== _serverTtsGeneration) {
              _ttsDiag("serverTts:discarded", {
                reason: "stale_generation_play_race",
                generation: _myGeneration, current: _serverTtsGeneration,
              });
              _clearCurrentAttachment();
              return;
            }
            _clearCurrentAttachment();
            completeVisibleOnly("play_rejected");
          });
        }
        // Single guarded fallback retry. If no measurable progress lands
        // within FALLBACK_RETRY_MS, retry play() once on this SAME
        // element/src — no reload, no second concurrent attempt. The
        // generation/_completionFired/_playbackStartedAt/_fallbackRetryAttempted
        // guards make this a no-op once progress or completion has
        // already happened, so it can never produce a second, overlapping
        // playback.
        _fallbackRetryTimer = setTimeout(() => {
          _fallbackRetryTimer = null;
          if (_myGeneration !== _serverTtsGeneration) return;
          if (_completionFired) return;
          if (_playbackStartedAt !== 0) return;
          if (_fallbackRetryAttempted) return;
          _fallbackRetryAttempted = true;
          _ttsDiag("serverTts:fallback_retry", {
            generation: _myGeneration,
            current_time: (() => { try { return a.currentTime; } catch { return null; } })(),
            paused: (() => { try { return a.paused; } catch { return null; } })(),
            ready_state: (() => { try { return a.readyState; } catch { return null; } })(),
            network_state: (() => { try { return a.networkState; } catch { return null; } })(),
          });
          try {
            const retryP = a.play();
            if (retryP && typeof retryP.catch === "function") {
              retryP.catch(() => {
                if (_myGeneration !== _serverTtsGeneration || _completionFired) return;
                _clearCurrentAttachment();
                completeVisibleOnly("play_rejected_after_retry");
              });
            }
          } catch (retryErr) {
            if (_myGeneration === _serverTtsGeneration && !_completionFired) {
              _clearCurrentAttachment();
              completeVisibleOnly("play_exception_after_retry");
            }
          }
        }, FALLBACK_RETRY_MS);
      } catch (e) {
        _clearCurrentAttachment();
        completeVisibleOnly("play_exception");
      }
    } catch (e) {
      // Outer fetch error / abort. Stale-check first so a cancel
      // during an in-flight fetch reads as discarded, not fallback.
      if (_myGeneration !== _serverTtsGeneration) {
        _ttsDiag("serverTts:discarded", {
          reason: "stale_generation_fetch_error",
          generation: _myGeneration,
          current: _serverTtsGeneration,
        });
        _fireEnd("stale_generation");
        return;
      }
      completeVisibleOnly(e && e.name === "AbortError" ? "timeout" : "fetch_error");
    }
  })();
  return true;
};

// ---------- Diagnostic snapshot (dev panel) ----------
export const diagSnapshot = () => {
  try {
    return {
      tts_enabled: isTtsEnabled(),
      pref_raw: readPrefRaw(),
      primed: _primed,
      voices_available: _voicesCache.length,
      speaking: !!window.speechSynthesis?.speaking,
      pending: !!window.speechSynthesis?.pending,
      paused: !!window.speechSynthesis?.paused,
      recent: getDiag(20),
    };
  } catch {
    return { error: "unavailable" };
  }
};

// Exported for unit tests only.
const __resetForTest = () => {
  _persistentAudio = null;
  _persistentAudioUnlockAttempt += 1;
  _persistentAudioUnlocked = false;
  _persistentAudioUnlockInFlight = false;
  _persistentAudioUnlockPromise = null;
  _persistentAudioUnlockError = null;
  _currentPersistentBlobUrl = null;
  _serverTtsGeneration = 0;
  _currentServerAudioOnEnd = null;
  _serverTtsSessionUnavailable = false;
  _primed = false;
  _diagBuf.length = 0;
  _turnDiagBuckets.clear();
  _reportableTurnId = null;
  try {
    if (typeof _activeWatchdogCleanup === "function") {
      _activeWatchdogCleanup("test_reset");
    }
  } catch { /* noop */ }
  _activeWatchdogCleanup = null;
};
const __getPersistentAudioForTest = () => _persistentAudio;
const __getPersistentUnlockedForTest = () => _persistentAudioUnlocked;
const __getPersistentUnlockInFlightForTest = () => _persistentAudioUnlockInFlight;
const __getPersistentPrimerSettleMsForTest = () => PERSISTENT_PRIMER_SETTLE_MS;
const __getPersistentPrimerForTest = () => ({
  src: _SILENT_UNLOCK_WAV,
  sample_rate: _PERSISTENT_PRIMER_SAMPLE_RATE,
  channels: _PERSISTENT_PRIMER_CHANNELS,
  bits_per_sample: _PERSISTENT_PRIMER_BITS_PER_SAMPLE,
  frame_count: _PERSISTENT_PRIMER_FRAME_COUNT,
  duration_ms: _PERSISTENT_PRIMER_DURATION_MS,
});
const __getServerTtsGenerationForTest = () => _serverTtsGeneration;
const __getCurrentBlobUrlForTest = () => _currentPersistentBlobUrl;
const __getWatchdogArmedForTest = () => _activeWatchdogCleanup !== null;
const __getWatchdogThresholdsForTest = () => ({
  progress_stall_ms: WATCHDOG_PROGRESS_STALL_MS,
  pause_stall_ms: WATCHDOG_PAUSE_STALL_MS,
  absolute_min_ms: WATCHDOG_ABSOLUTE_MIN_MS,
  absolute_pad_ms: WATCHDOG_ABSOLUTE_PAD_MS,
  tick_ms: WATCHDOG_TICK_MS,
});
export const __internals = {
  pickEnglishVoice, pickSpanishVoice, EN_US_MALE_NAMES, ES_PREFERRED_NAMES, EN_EXCLUDE,
  __resetForTest,
  __getPersistentAudioForTest,
  __getPersistentUnlockedForTest,
  __getPersistentUnlockInFlightForTest,
  __getPersistentPrimerSettleMsForTest,
  __getPersistentPrimerForTest,
  __getServerTtsGenerationForTest,
  __getCurrentBlobUrlForTest,
  __getWatchdogArmedForTest,
  __getWatchdogThresholdsForTest,
};
